// Guitar Theory & Data for Melodalis Kawaii Guitar Academy

export interface GuitarStringInfo {
  stringNumber: number; // 1 to 6 (1 is high E, 6 is low E)
  name: string;
  baseNote: string;
  baseOctave: number;
  baseFreq: number;
}

export const GUITAR_STRINGS: GuitarStringInfo[] = [
  { stringNumber: 1, name: '1st (High E)', baseNote: 'E', baseOctave: 4, baseFreq: 329.63 },
  { stringNumber: 2, name: '2nd (B)', baseNote: 'B', baseOctave: 3, baseFreq: 246.94 },
  { stringNumber: 3, name: '3rd (G)', baseNote: 'G', baseOctave: 3, baseFreq: 196.00 },
  { stringNumber: 4, name: '4th (D)', baseNote: 'D', baseOctave: 3, baseFreq: 146.83 },
  { stringNumber: 5, name: '5th (A)', baseNote: 'A', baseOctave: 2, baseFreq: 110.00 },
  { stringNumber: 6, name: '6th (Low E)', baseNote: 'E', baseOctave: 2, baseFreq: 82.41 },
];

export const CHROMATIC_NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

export function getNoteAtFret(stringIndex: number, fret: number): { 
  note: string; 
  freq: number; 
  isNatural: boolean;
  octave: number;
  noteWithOctave: string;
} {
  const str = GUITAR_STRINGS[stringIndex];
  const freq = str.baseFreq * Math.pow(2, fret / 12);
  const startIdx = CHROMATIC_NOTES.indexOf(str.baseNote);
  const totalSemitones = startIdx + fret;
  const noteIdx = totalSemitones % 12;
  const note = CHROMATIC_NOTES[noteIdx];
  const isNatural = !note.includes('#');
  const octave = str.baseOctave + Math.floor(totalSemitones / 12);
  return { note, freq, isNatural, octave, noteWithOctave: `${note}${octave}` };
}

export interface StringTheme {
  stringIdx: number;
  name: string;
  colorName: string;
  badgeBg: string;
  badgeBorder: string;
  textColor: string;
  stringLineGradient: string;
  chipBg: string;
  chipText: string;
}

export const STRING_THEMES: StringTheme[] = [
  {
    stringIdx: 0,
    name: '1st (High E)',
    colorName: 'Rose Pink 🌸',
    badgeBg: 'bg-rose-500',
    badgeBorder: 'border-rose-300',
    textColor: 'text-white',
    stringLineGradient: 'from-rose-300 via-rose-400 to-rose-300',
    chipBg: 'bg-rose-100',
    chipText: 'text-rose-700',
  },
  {
    stringIdx: 1,
    name: '2nd (B)',
    colorName: 'Lavender 💜',
    badgeBg: 'bg-purple-500',
    badgeBorder: 'border-purple-300',
    textColor: 'text-white',
    stringLineGradient: 'from-purple-300 via-purple-400 to-purple-300',
    chipBg: 'bg-purple-100',
    chipText: 'text-purple-700',
  },
  {
    stringIdx: 2,
    name: '3rd (G)',
    colorName: 'Sky Blue 🩵',
    badgeBg: 'bg-sky-500',
    badgeBorder: 'border-sky-300',
    textColor: 'text-white',
    stringLineGradient: 'from-sky-300 via-sky-400 to-sky-300',
    chipBg: 'bg-sky-100',
    chipText: 'text-sky-700',
  },
  {
    stringIdx: 3,
    name: '4th (D)',
    colorName: 'Mint Green 🍃',
    badgeBg: 'bg-emerald-500',
    badgeBorder: 'border-emerald-300',
    textColor: 'text-white',
    stringLineGradient: 'from-emerald-300 via-emerald-400 to-emerald-300',
    chipBg: 'bg-emerald-100',
    chipText: 'text-emerald-700',
  },
  {
    stringIdx: 4,
    name: '5th (A)',
    colorName: 'Sunshine Yellow ☀️',
    badgeBg: 'bg-amber-400',
    badgeBorder: 'border-amber-300',
    textColor: 'text-amber-950',
    stringLineGradient: 'from-amber-300 via-amber-400 to-amber-300',
    chipBg: 'bg-amber-100',
    chipText: 'text-amber-900',
  },
  {
    stringIdx: 5,
    name: '6th (Low E)',
    colorName: 'Warm Coral 🍑',
    badgeBg: 'bg-orange-500',
    badgeBorder: 'border-orange-300',
    textColor: 'text-white',
    stringLineGradient: 'from-orange-300 via-orange-400 to-orange-300',
    chipBg: 'bg-orange-100',
    chipText: 'text-orange-800',
  },
];

export type ChordCategory = 'Major' | 'Minor' | '7th' | 'Maj7 & Min7' | 'Diminished' | 'Suspended';

export interface ChordData {
  id: string;
  name: string;
  symbol: string;
  root: 'C' | 'D' | 'E' | 'F' | 'G' | 'A' | 'B';
  category: ChordCategory;
  difficulty: 'Super Easy 🐣' | 'Easy 🌸' | 'Medium ⭐';
  description: string;
  kawaiiVibe: string;
  // Frets for strings [1st (High E), 2nd (B), 3rd (G), 4th (D), 5th (A), 6th (Low E)]
  // -1 means Mute (X), 0 means Open, >0 is fret number
  frets: [number, number, number, number, number, number];
  // Fingers: 1=Index, 2=Middle, 3=Ring, 4=Pinky, 0=None
  fingers: [number, number, number, number, number, number];
  tips?: string;
}

export const GUITAR_CHORDS: ChordData[] = [
  // --- MAJOR CHORDS ---
  {
    id: 'c-major',
    name: 'C Major',
    symbol: 'C',
    root: 'C',
    category: 'Major',
    difficulty: 'Easy 🌸',
    description: 'Bright, happy, and one of the most loved chords in pop and kawaii songs!',
    kawaiiVibe: 'Sunshine & rainbow drops 🌈',
    frets: [0, 1, 0, 2, 3, -1],
    fingers: [0, 1, 0, 2, 3, 0],
  },
  {
    id: 'g-major',
    name: 'G Major',
    symbol: 'G',
    root: 'G',
    category: 'Major',
    difficulty: 'Easy 🌸',
    description: 'Rich, full, and energetic! Rings across all 6 strings beautifully.',
    kawaiiVibe: 'Warm morning breeze 🌻',
    frets: [3, 0, 0, 0, 2, 3],
    fingers: [3, 0, 0, 0, 1, 2],
  },
  {
    id: 'd-major',
    name: 'D Major',
    symbol: 'D',
    root: 'D',
    category: 'Major',
    difficulty: 'Easy 🌸',
    description: 'Crisp and chimey like tiny fairy bells! Forms a cute little triangle shape.',
    kawaiiVibe: 'Sparkling fairy lights ✨',
    frets: [2, 3, 2, 0, -1, -1],
    fingers: [2, 3, 1, 0, 0, 0],
  },
  {
    id: 'a-major',
    name: 'A Major',
    symbol: 'A',
    root: 'A',
    category: 'Major',
    difficulty: 'Easy 🌸',
    description: 'Three little finger buddies squished happily on the same 2nd fret!',
    kawaiiVibe: 'Happy picnic with friends 🥪',
    frets: [0, 2, 2, 2, 0, -1],
    fingers: [0, 3, 2, 1, 0, 0],
  },
  {
    id: 'e-major',
    name: 'E Major',
    symbol: 'E',
    root: 'E',
    category: 'Major',
    difficulty: 'Easy 🌸',
    description: 'A triumphant, powerful chord that vibrates the entire guitar body!',
    kawaiiVibe: 'Heroic magical girl transformation 💖',
    frets: [0, 0, 1, 2, 2, 0],
    fingers: [0, 0, 1, 3, 2, 0],
  },
  {
    id: 'f-major-easy',
    name: 'F Major (Easy/Mini)',
    symbol: 'F',
    root: 'F',
    category: 'Major',
    difficulty: 'Medium ⭐',
    description: 'The friendly beginner version of the F chord! Sounds sweet & warm.',
    kawaiiVibe: 'Little pastel blossom 🌸',
    frets: [1, 1, 2, 3, -1, -1],
    fingers: [1, 1, 2, 3, 0, 0],
  },
  {
    id: 'f-major-barre',
    name: 'F Major (Full Barre)',
    symbol: 'F (Barre)',
    root: 'F',
    category: 'Major',
    difficulty: 'Medium ⭐',
    description: 'The iconic full barre chord across all 6 frets. Strong, solid, and punchy.',
    kawaiiVibe: 'Confident mountain peak 🏔️',
    frets: [1, 1, 2, 3, 3, 1],
    fingers: [1, 1, 2, 4, 3, 1],
  },
  {
    id: 'b-major',
    name: 'B Major',
    symbol: 'B',
    root: 'B',
    category: 'Major',
    difficulty: 'Medium ⭐',
    description: 'An uplifting pop chord that bridges the gap between G and E!',
    kawaiiVibe: 'Bright neon candy 🍬',
    frets: [2, 4, 4, 4, 2, -1],
    fingers: [1, 4, 3, 2, 1, 0],
  },

  // --- MINOR CHORDS ---
  {
    id: 'e-minor',
    name: 'E Minor',
    symbol: 'Em',
    root: 'E',
    category: 'Minor',
    difficulty: 'Super Easy 🐣',
    description: 'Deep, emotional, and only needs TWO fingers! The best chord to learn first.',
    kawaiiVibe: 'Cozy rainy day with hot cocoa ☕',
    frets: [0, 0, 0, 2, 2, 0],
    fingers: [0, 0, 0, 2, 1, 0],
  },
  {
    id: 'a-minor',
    name: 'A Minor',
    symbol: 'Am',
    root: 'A',
    category: 'Minor',
    difficulty: 'Easy 🌸',
    description: 'Sweet and melancholic, perfect for heartfelt acoustic ballads and pop intros.',
    kawaiiVibe: 'Gentle starry night 🌙',
    frets: [0, 1, 2, 2, 0, -1],
    fingers: [0, 1, 3, 2, 0, 0],
  },
  {
    id: 'd-minor',
    name: 'D Minor',
    symbol: 'Dm',
    root: 'D',
    category: 'Minor',
    difficulty: 'Medium ⭐',
    description: 'Dramatic and cinematic. Gives songs a magical, mysterious flavor.',
    kawaiiVibe: 'Secret enchanted forest 🍃',
    frets: [1, 3, 2, 0, -1, -1],
    fingers: [1, 3, 2, 0, 0, 0],
  },
  {
    id: 'b-minor',
    name: 'B Minor',
    symbol: 'Bm',
    root: 'B',
    category: 'Minor',
    difficulty: 'Medium ⭐',
    description: 'Emotional, bittersweet, and widely used in rock, ballads, and pop verses.',
    kawaiiVibe: 'Autumn leaves in the wind 🍂',
    frets: [2, 3, 4, 4, 2, -1],
    fingers: [1, 2, 4, 3, 1, 0],
  },
  {
    id: 'f-minor',
    name: 'F Minor',
    symbol: 'Fm',
    root: 'F',
    category: 'Minor',
    difficulty: 'Medium ⭐',
    description: 'Moody, deeply poignant, and full of intense cinematic feeling.',
    kawaiiVibe: 'Midnight lavender sky 🌌',
    frets: [1, 1, 1, 3, 3, 1],
    fingers: [1, 1, 1, 4, 3, 1],
  },

  // --- 7TH / DOMINANT CHORDS ---
  {
    id: 'c7',
    name: 'C Dominant 7th',
    symbol: 'C7',
    root: 'C',
    category: '7th',
    difficulty: 'Medium ⭐',
    description: 'A jazzy, groovy twist on C Major with an added pinky note.',
    kawaiiVibe: 'Bubbly retro cafe swing 🎷',
    frets: [0, 1, 3, 2, 3, -1],
    fingers: [0, 1, 4, 2, 3, 0],
  },
  {
    id: 'a7',
    name: 'A Dominant 7th',
    symbol: 'A7',
    root: 'A',
    category: '7th',
    difficulty: 'Super Easy 🐣',
    description: 'Bluesy, warm, and needs only two fingers with an open middle string!',
    kawaiiVibe: 'Lazy afternoon acoustic porch 🎸',
    frets: [0, 2, 0, 2, 0, -1],
    fingers: [0, 2, 0, 1, 0, 0],
  },
  {
    id: 'd7',
    name: 'D Dominant 7th',
    symbol: 'D7',
    root: 'D',
    category: '7th',
    difficulty: 'Easy 🌸',
    description: 'An inverted triangle shape that pulls smoothly back to G Major.',
    kawaiiVibe: 'Sparkling musical pendulum ⏳',
    frets: [2, 1, 2, 0, -1, -1],
    fingers: [3, 1, 2, 0, 0, 0],
  },
  {
    id: 'e7',
    name: 'E Dominant 7th',
    symbol: 'E7',
    root: 'E',
    category: '7th',
    difficulty: 'Super Easy 🐣',
    description: 'The easiest blues chord in the world! Just one or two fingers on the fretboard.',
    kawaiiVibe: 'Winking sunshine cat 🐱',
    frets: [0, 0, 1, 0, 2, 0],
    fingers: [0, 0, 1, 0, 2, 0],
  },
  {
    id: 'g7',
    name: 'G Dominant 7th',
    symbol: 'G7',
    root: 'G',
    category: '7th',
    difficulty: 'Easy 🌸',
    description: 'A big, joyful chord that yearns to resolve happily into C Major!',
    kawaiiVibe: 'Carnival carousel ride 🎠',
    frets: [1, 0, 0, 0, 2, 3],
    fingers: [1, 0, 0, 0, 2, 3],
  },
  {
    id: 'b7',
    name: 'B Dominant 7th',
    symbol: 'B7',
    root: 'B',
    category: '7th',
    difficulty: 'Medium ⭐',
    description: 'Rich, classy, and beloved in acoustic fingerpicking and folk tunes.',
    kawaiiVibe: 'Vintage music box charm 🎶',
    frets: [2, 0, 2, 1, 2, -1],
    fingers: [4, 0, 3, 1, 2, 0],
  },

  // --- MAJOR 7TH & MINOR 7TH ---
  {
    id: 'cmaj7',
    name: 'C Major 7th',
    symbol: 'Cmaj7',
    root: 'C',
    category: 'Maj7 & Min7',
    difficulty: 'Super Easy 🐣',
    description: 'Dreamy, ethereal, and relaxing! Lift your index finger off standard C.',
    kawaiiVibe: 'Floating on a pastel cloud ☁️',
    frets: [0, 0, 0, 2, 3, -1],
    fingers: [0, 0, 0, 2, 3, 0],
  },
  {
    id: 'fmaj7',
    name: 'F Major 7th',
    symbol: 'Fmaj7',
    root: 'F',
    category: 'Maj7 & Min7',
    difficulty: 'Easy 🌸',
    description: 'Sweet, lush, and gentle without requiring any barre finger at all.',
    kawaiiVibe: 'Cherry blossom petals falling 🌸',
    frets: [0, 1, 2, 3, -1, -1],
    fingers: [0, 1, 2, 3, 0, 0],
  },
  {
    id: 'am7',
    name: 'A Minor 7th',
    symbol: 'Am7',
    root: 'A',
    category: 'Maj7 & Min7',
    difficulty: 'Super Easy 🐣',
    description: 'Soft, soulful, and breezy. Only uses two fingertips!',
    kawaiiVibe: 'Warm cup of lavender tea 🫖',
    frets: [0, 1, 0, 2, 0, -1],
    fingers: [0, 1, 0, 2, 0, 0],
  },
  {
    id: 'em7',
    name: 'E Minor 7th',
    symbol: 'Em7',
    root: 'E',
    category: 'Maj7 & Min7',
    difficulty: 'Super Easy 🐣',
    description: 'Can be played with just ONE finger! Open, resonant, and peaceful.',
    kawaiiVibe: 'Sleeping bunny in the meadow 🐇',
    frets: [0, 0, 0, 0, 2, 0],
    fingers: [0, 0, 0, 0, 1, 0],
  },
  {
    id: 'dm7',
    name: 'D Minor 7th',
    symbol: 'Dm7',
    root: 'D',
    category: 'Maj7 & Min7',
    difficulty: 'Easy 🌸',
    description: 'Tender and reflective, perfect for soulful lo-fi acoustic vibes.',
    kawaiiVibe: 'Raindrops on window glass 💧',
    frets: [1, 1, 2, 0, -1, -1],
    fingers: [1, 1, 2, 0, 0, 0],
  },

  // --- DIMINISHED & SUSPENDED ---
  {
    id: 'cadd9',
    name: 'C Add 9',
    symbol: 'Cadd9',
    root: 'C',
    category: 'Suspended',
    difficulty: 'Easy 🌸',
    description: 'The golden pop chord! Rings in modern acoustic hits like magic.',
    kawaiiVibe: 'Sparkling diamond dust 💎',
    frets: [3, 3, 0, 2, 3, -1],
    fingers: [4, 3, 0, 1, 2, 0],
  },
  {
    id: 'dsus2',
    name: 'D Suspended 2nd',
    symbol: 'Dsus2',
    root: 'D',
    category: 'Suspended',
    difficulty: 'Super Easy 🐣',
    description: 'Crisp, airy, and floats peacefully between open strings.',
    kawaiiVibe: 'Morning dewdrop on a leaf 🍃',
    frets: [0, 3, 2, 0, -1, -1],
    fingers: [0, 3, 1, 0, 0, 0],
  },
  {
    id: 'dsus4',
    name: 'D Suspended 4th',
    symbol: 'Dsus4',
    root: 'D',
    category: 'Suspended',
    difficulty: 'Easy 🌸',
    description: 'A suspenseful, uplifting pop chord that leads right into D Major.',
    kawaiiVibe: 'Anticipation before the chorus 🎈',
    frets: [3, 3, 2, 0, -1, -1],
    fingers: [4, 3, 1, 0, 0, 0],
  },
  {
    id: 'asus4',
    name: 'A Suspended 4th',
    symbol: 'Asus4',
    root: 'A',
    category: 'Suspended',
    difficulty: 'Easy 🌸',
    description: 'Warm, rising energy that blooms open into A Major.',
    kawaiiVibe: 'Sunbeam breaking through clouds ⛅',
    frets: [0, 3, 2, 2, 0, -1],
    fingers: [0, 4, 3, 2, 0, 0],
  },
  {
    id: 'b-diminished',
    name: 'B Diminished',
    symbol: 'Bdim',
    root: 'B',
    category: 'Diminished',
    difficulty: 'Medium ⭐',
    description: 'A tense, mystical, and mysterious chord that pulls magically toward C Major!',
    kawaiiVibe: 'Curious wizard potion 🧪',
    frets: [-1, 3, 4, 3, 2, -1],
    fingers: [0, 2, 4, 3, 1, 0],
  },
  {
    id: 'd-diminished',
    name: 'D Diminished',
    symbol: 'Ddim',
    root: 'D',
    category: 'Diminished',
    difficulty: 'Medium ⭐',
    description: 'Haunting and expressive chord used in classical and dramatic acoustic songs.',
    kawaiiVibe: 'Moonlight shadows 🎭',
    frets: [1, 3, 1, 0, -1, -1],
    fingers: [1, 3, 2, 0, 0, 0],
  },
];

export interface ChordProgressionLevel {
  level: 1 | 2 | 3 | 4;
  title: string;
  badge: string;
  kawaiiMood: string;
  difficultyLabel: string;
  description: string;
  famousFor: string;
  chordIds: string[]; // 4 chord IDs in sequence
}

export const FOUR_CHORD_PROGRESSIONS: ChordProgressionLevel[] = [
  {
    level: 1,
    title: 'Golden Sunshine (2-Finger Warmup)',
    badge: 'Level 1: Absolute Easiest 🐣',
    kawaiiMood: 'Pure joy & easy open strings 🌈',
    difficultyLabel: 'Only 1 or 2 fingers per shape — no buzzing!',
    description: 'The easiest, sweetest 4-chord sequence to begin playing guitar! Strings ring freely with almost zero hand movement.',
    famousFor: 'Acoustic campfire melodies, kawaii pop lullabies, and easy strums',
    chordIds: ['e-minor', 'g-major', 'cadd9', 'd-major'],
  },
  {
    level: 2,
    title: 'The Legendary Pop Hit Sequence',
    badge: 'Level 2: Friendly Pop 🌸',
    kawaiiMood: 'Feel-good radio energy 📻',
    difficultyLabel: 'Standard open beginner shapes',
    description: 'The single most famous chord formula in music history! Thousands of global pop hits and Disney songs use this exact loop.',
    famousFor: 'Taylor Swift, Disney anthems, "Let It Be", "Don\'t Stop Believin\'"',
    chordIds: ['c-major', 'g-major', 'a-minor', 'f-major-easy'],
  },
  {
    level: 3,
    title: 'The 50s Doo-Wop & Heartfelt Ballad',
    badge: 'Level 3: Sweet & Romantic 💖',
    kawaiiMood: 'Nostalgic sweetness 🎀',
    difficultyLabel: 'Smooth Major-to-Minor transitions',
    description: 'A timeless progression that takes you on an emotional journey from radiant joy through sweet sentimental melody.',
    famousFor: 'Classic love songs, anime ending themes, acoustic ballads',
    chordIds: ['g-major', 'e-minor', 'c-major', 'd-major'],
  },
  {
    level: 4,
    title: 'The Dramatic Songwriter Cycle',
    badge: 'Level 4: Expressive & Moody ⭐',
    kawaiiMood: 'Cinematic adventure 🍃',
    difficultyLabel: 'Minor start into uplifting release',
    description: 'Starts in a deep, introspective minor feeling, building through sweet harmony into a powerful, inspiring resolution.',
    famousFor: 'Dramatic pop verses, indie acoustic songs, video game themes',
    chordIds: ['a-minor', 'f-major-easy', 'c-major', 'g-major'],
  },
];

export function getChordFrequencies(chord: ChordData): (number | null)[] {
  // frets: [highE, B, G, D, A, lowE]
  // In GUITAR_STRINGS: [0: High E, 1: B, 2: G, 3: D, 4: A, 5: Low E]
  // We return low to high for strumming: Low E -> High E
  const result: (number | null)[] = [];
  for (let s = 5; s >= 0; s--) {
    const fret = chord.frets[s];
    if (fret === -1) {
      result.push(null);
    } else {
      const str = GUITAR_STRINGS[s];
      result.push(str.baseFreq * Math.pow(2, fret / 12));
    }
  }
  return result;
}
